import { BoxProps, Box } from "@vinomofo/components";
import styled, { keyframes } from "styled-components";
import React, {
  useState,
  useEffect,
  useMemo,
  useCallback,
  useRef,
  CSSProperties,
} from "react";
import {
  BellIcon as BellVinoIcon,
  BellRungIcon as BellRungVinoIcon,
} from "@vinomofo/icons";

// Define animations
const ringAnimation = keyframes`
  0% { transform: rotate(0deg); }
  20% { transform: rotate(-15deg); }
  40% { transform: rotate(15deg); }
  60% { transform: rotate(-10deg); }
  80% { transform: rotate(10deg); }
  100% { transform: rotate(0deg); }
`;

const zoomAnimation = keyframes`
  0% { transform: scale(1); }
  50% { transform: scale(1.2); }
  100% { transform: scale(1); }
`;

const BellIcon = styled.button<{ disabled: boolean }>`
  width: 25px;
  z-index: 10;
  cursor: ${(props) => (props.disabled ? "not-allowed" : "pointer")};
  border: none;
  background: none;
  display: flex;
  justify-content: center;
  align-items: center;
  opacity: ${(props) => (props.disabled ? 0.2 : 1)};
  pointer-events: ${(props) => (props.disabled ? "none" : "auto")};
  position: relative;

  svg {
    position: absolute;
    top: 10px;
    left: 0;
    right: 0;
    bottom: 0;
    opacity: 0;
    margin: 0 auto;
    fill: #48a90f;
  }

  &.show-bell .icon-wrapper.bell svg {
    opacity: 1;
  }

  &.show-bell-rung .icon-wrapper.bell-rung svg {
    opacity: 1;
    animation: ${zoomAnimation} 0.5s ease, ${ringAnimation} 0.6s ease;
  }

  &:hover svg {
    fill: ${(props) => (props.disabled ? "#48A90F" : "#55CC00")};
  }
`;

interface Props extends BoxProps {
  isMyOffer?: boolean;
  onSubscribe: () => void;
  onUnsubscribe: () => void;
  active: boolean;
  setPendingRemoval?: React.Dispatch<React.SetStateAction<boolean>>;
  pendingRemoval?: boolean;
  style?: CSSProperties;
  size?: string;
  user_id?: string;
}

export const BellToggleV2 = ({
  isMyOffer,
  onSubscribe,
  onUnsubscribe,
  active = false,
  setPendingRemoval,
  pendingRemoval,
  size,
  user_id,
  ...rest
}: Props): JSX.Element => {
  const [isSubscribed, setIsSubscribed] = useState(active);
  const [disableBtn, setDisableBtn] = useState(false);
  const removalTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Memoize the SVG elements to avoid unnecessary re-renders
  const bellIcon = useMemo(
    () => (
      <>
        <div className="icon-wrapper bell">
          {React.cloneElement(<BellVinoIcon />, {
            size: size ? size : "22",
          })}
        </div>
        <div className="icon-wrapper bell-rung">
          {React.cloneElement(<BellRungVinoIcon />, {
            size: size ? size : "22",
          })}
        </div>
      </>
    ),
    [size]
  );

  // Memoize the click handler
  const handleIconClick = useCallback(() => {
    if (isMyOffer) {
      if (!pendingRemoval) {
        setPendingRemoval?.(true);
        if (isSubscribed) {
          removalTimer.current = setTimeout(async () => {
            onUnsubscribe();
            setPendingRemoval?.(false);
            setDisableBtn(true);
          }, 3000);
        }
      } else {
        if (removalTimer.current !== null) {
          clearTimeout(removalTimer.current);
        }
        setPendingRemoval?.(false);
      }
    } else {
      isSubscribed ? onUnsubscribe() : onSubscribe();
    }
    if (user_id) setIsSubscribed((prev) => !prev);
  }, [
    isSubscribed,
    onSubscribe,
    onUnsubscribe,
    user_id,
    pendingRemoval,
    setPendingRemoval,
    isMyOffer,
  ]);

  useEffect(() => {
    return () => {
      if (removalTimer.current) {
        clearTimeout(removalTimer.current);
      }
    };
  }, []);

  useEffect(() => {
    if (isMyOffer) {
      setIsSubscribed(true);
    } else {
      setIsSubscribed(active);
    }
  }, [active]);

  return (
    <BellIcon
      className={`${isSubscribed ? "show-bell-rung" : "show-bell"}`}
      onClick={handleIconClick}
      disabled={disableBtn}
      {...rest}
    >
      {bellIcon}
    </BellIcon>
  );
};
